import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import '../../layout-flex/types/index.esm.js';
import { EGDSLayoutFlex } from '../../layout-flex/layout-flex/index.esm.js';
import { EGDSLayoutFlexItem } from '../../layout-flex/layout-flex-item/index.esm.js';
import '../../utils/index_orig/index.esm.js';
import '../../utils/theme/theme-provider/index.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import styles from '../../styles/uitk-egds-sheet.css';
import { EGDSSheetPositionTypes } from '../types/index.esm.js';
import EGDSSheetContext from '../sheet-context/index.esm.js';
import { isValidEGDSSheetPosition, attachHandlers, setElementFocus, detachHandlers, setFocus, updateSheetProps, defineSheetLayout } from '../sheet-utilities/index.esm.js';
import { classNames } from '../../utils/classnames/index.esm.js';
import { liveAnnounce, Level } from '../../utils/live-announce/index.esm.js';
import { MakeEgdsSizingLength } from '../../utils/egds-sizing-length/index.esm.js';
const getClassNameList = classNames.bind(styles);
const EGDSPopoverSheet = props => {
  const {
    ariaLabel,
    autoFocus = true,
    autoPosition = true,
    children,
    className,
    fullWidth = false,
    hasModal,
    intersectionObserverRoot,
    isVisible,
    maxHeight,
    onDismiss,
    disableBodyClickHandler,
    disableArrowKeyHandler,
    position: passedInPosition = {
      block: EGDSSheetPositionTypes.TRAILING,
      inline: EGDSSheetPositionTypes.LEADING
    },
    applyBlockStartMargin = false,
    showOverTrigger = false,
    returnFocusOnClose = true,
    triggerRef,
    focusElemRef,
    type = 'popover',
    width,
    ...attributes
  } = props;
  const propsRef = React.useRef({
    onDismiss,
    triggerRef,
    disableArrowKeyHandler,
    disableBodyClickHandler,
    hasModal
  });
  const prevOffsetStoreRef = React.useRef({
    x: 0,
    y: 0
  });
  const sheetRef = React.useRef(null);
  const intersectionObserverRootRef = React.useRef(null);
  const [animationHasEnded, setAnimationHasEnded] = React.useState(false);
  const blockPosition = passedInPosition.block && isValidEGDSSheetPosition(passedInPosition.block) ? passedInPosition.block : EGDSSheetPositionTypes.TRAILING;
  const inlinePosition = passedInPosition.inline && isValidEGDSSheetPosition(passedInPosition.inline) ? passedInPosition.inline : EGDSSheetPositionTypes.LEADING;
  // State hooks
  const [offset, setOffset] = React.useState({
    x: 0,
    y: 0
  });
  const baseClassName = `uitk-${type}-sheet`;
  const sheetClassNames = getClassNameList(baseClassName, className, `${baseClassName}-inline-pos-${inlinePosition}`, `${baseClassName}-block-pos-${blockPosition}`, {
    [`${baseClassName}-full-width`]: fullWidth
  }, {
    [`${baseClassName}-open`]: isVisible
  }, {
    [`${baseClassName}-has-intersection-root-el`]: autoPosition && intersectionObserverRoot
  }, {
    [`${baseClassName}-has-block-start-margin`]: blockPosition === EGDSSheetPositionTypes.TRAILING && applyBlockStartMargin
  });
  let intersectionObserver;
  const unobserveIntersectionObserver = React.useCallback(() => {
    if (autoPosition && sheetRef.current) {
      intersectionObserver && intersectionObserver.unobserve(sheetRef.current);
    }
  }, [autoPosition, intersectionObserver]);
  const onAnimationEndCallback = e => {
    // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
    !animationHasEnded && e.animationName.match(/sheet-grow-/) && setAnimationHasEnded(true);
  };
  // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
  // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
  // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
  // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
  // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
  const animationEndedStateCallback = React.useCallback(() => {
    // eslint-disable-next-line no-negated-condition
    const viewport = typeof document !== 'undefined' ? document.documentElement : null;
    if (sheetRef?.current && viewport) {
      const container = sheetRef.current;
      const windowInnerHeight = window.innerHeight;
      const containerBoundingClientRect = container.getBoundingClientRect();
      const viewportBoundingClientRect = viewport.getBoundingClientRect();
      let newOffsetX = 0;
      let newOffsetY = 0;
      if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
        newOffsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
      } else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
        newOffsetX = Math.abs(containerBoundingClientRect.left);
      }
      if (blockPosition === EGDSSheetPositionTypes.TRAILING && containerBoundingClientRect.bottom > windowInnerHeight) {
        newOffsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
      } else if (blockPosition === EGDSSheetPositionTypes.LEADING && containerBoundingClientRect.top < 0) {
        newOffsetY = Math.abs(Math.ceil(containerBoundingClientRect.top));
      }
      if (newOffsetX || newOffsetY) {
        container.style.setProperty('--uitk-popover-sheet-offset-translate-x', `${newOffsetX}px`);
        container.style.setProperty('--uitk-popover-sheet-offset-translate-y', `${newOffsetY}px`);
      }
      unobserveIntersectionObserver(); // Unobserve after the initial re-aligment, so that the popover sheet doesn't end up far away from the trigger on scroll
    }
  }, [blockPosition, unobserveIntersectionObserver]);
  const adjustAlignment = entry => {
    const prevOffset = prevOffsetStoreRef.current;
    const isIntersectingOnRight = Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right);
    const isIntersectingOnLeft = Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left);
    const isIntersectingOnBottom = Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom);
    const isIntersectingOnTop = Math.ceil(entry.boundingClientRect.top) < Math.ceil(entry.intersectionRect.top);
    if (isIntersectingOnRight) {
      const currentOffsetX = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
      setOffset({
        x: -Math.abs(prevOffset.x - currentOffsetX),
        y: prevOffset.y
      });
    } else if (isIntersectingOnLeft) {
      const currentOffsetX = entry.boundingClientRect.left - entry.intersectionRect.left;
      setOffset({
        x: prevOffset.x - currentOffsetX,
        y: prevOffset.y
      });
    }
    if (isIntersectingOnBottom || isIntersectingOnTop) {
      const currentOffsetXRight = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
      const currentOffsetXLeft = entry.boundingClientRect.left - entry.intersectionRect.left;
      let offsetX = prevOffset.x;
      // If it is also intersecting on left or right, we need to adjust offset on X axis too
      if (isIntersectingOnLeft) {
        offsetX = prevOffset.x - currentOffsetXLeft;
      } else if (isIntersectingOnRight) {
        offsetX = -Math.abs(prevOffset.x - currentOffsetXRight);
      }
      if (blockPosition === EGDSSheetPositionTypes.TRAILING && isIntersectingOnBottom) {
        const currentOffsetY = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
        setOffset({
          x: offsetX,
          y: -Math.abs(prevOffset.y - currentOffsetY)
        });
      } else if (blockPosition === EGDSSheetPositionTypes.LEADING && isIntersectingOnTop) {
        const currentOffsetY = Math.ceil(entry.boundingClientRect.top) - Math.ceil(entry.intersectionRect.top);
        setOffset({
          x: offsetX,
          y: prevOffset.y - currentOffsetY
        });
      }
    }
  };
  const autoPositionCallback = entries => {
    entries.forEach(entry => {
      if (entry.intersectionRatio < 0.99 && entry.intersectionRatio > 0) {
        adjustAlignment(entry);
      }
    });
  };
  const autoPositionObserver = React.useCallback(() => {
    if (!(window && window.IntersectionObserver)) {
      return false;
    }
    if (intersectionObserverRoot) {
      intersectionObserverRootRef.current = document.querySelector(intersectionObserverRoot);
    }
    const thresholdSet = [];
    for (let i = 0; i <= 1.0; i += 0.01) {
      thresholdSet.push(i);
    }
    const defaultIntersectionObserverOptions = {
      threshold: thresholdSet
    };
    const intersectionObserverOptions = intersectionObserverRoot ? Object.assign(defaultIntersectionObserverOptions, {
      root: intersectionObserverRootRef.current
    }) : defaultIntersectionObserverOptions;
    if (!intersectionObserver) {
      intersectionObserver = new IntersectionObserver(autoPositionCallback, intersectionObserverOptions);
    }
    sheetRef.current && intersectionObserver.observe(sheetRef.current);
  }, [intersectionObserverRoot]);
  // Hooks
  React.useEffect(() => {
    if (isVisible) {
      attachHandlers(props, sheetRef);
      // Reset liveannounce so the liveannounce element is not navigable by screenreader.
      liveAnnounce('', Level.POLITE);
      autoPosition && autoPositionObserver();
    }
  }, [autoFocus, autoPosition, autoPositionObserver, isVisible, hasModal, sheetRef, props]);
  // On mount
  React.useEffect(() => {
    // Set focus
    if (autoFocus && isVisible) {
      setElementFocus(sheetRef, false, focusElemRef);
    }
  }, [autoFocus, isVisible]);
  React.useEffect(() => {
    prevOffsetStoreRef.current = offset;
  }, [offset]);
  React.useEffect(() => {
    animationHasEnded && animationEndedStateCallback();
  }, [animationHasEnded, animationEndedStateCallback]);
  React.useEffect(() => {
    document.addEventListener('scroll', unobserveIntersectionObserver);
    // Clean-up
    return () => {
      document.removeEventListener('scroll', unobserveIntersectionObserver);
    };
  }, [unobserveIntersectionObserver]);
  // "unmount" hook
  React.useEffect(() => {
    if (!isVisible) {
      document.removeEventListener('scroll', unobserveIntersectionObserver);
      detachHandlers(sheetRef);
      unobserveIntersectionObserver();
      setOffset({
        x: 0,
        y: 0
      });
      setAnimationHasEnded(false);
      sheetRef?.current?.style.removeProperty('--uitk-popover-sheet-offset-translate-x');
      sheetRef?.current?.style.removeProperty('--uitk-popover-sheet-offset-translate-y');
      if (returnFocusOnClose) {
        setFocus(triggerRef);
      }
    }
  }, [isVisible, unobserveIntersectionObserver, returnFocusOnClose, triggerRef]);
  // Detach handlers on unmount
  React.useEffect(() => () => {
    detachHandlers(sheetRef);
  }, []);
  // Update sheet reference props
  React.useEffect(() => {
    propsRef.current = {
      onDismiss,
      triggerRef,
      disableArrowKeyHandler,
      disableBodyClickHandler,
      hasModal
    };
    updateSheetProps(sheetRef, propsRef.current);
  }, [onDismiss, triggerRef, disableArrowKeyHandler, disableBodyClickHandler, hasModal]);
  const {
    hasCloseButton,
    layoutProps,
    closeButtonLayoutProps,
    closeButtonElem,
    childrenElem
  } = defineSheetLayout(children);
  const posStyle = {
    ...(triggerRef?.current && triggerRef.current.offsetParent && sheetRef?.current && {
      [`--uitk-popover-sheet-block-pos`]: blockPosition === EGDSSheetPositionTypes.LEADING ? `${triggerRef.current.offsetTop - (sheetRef.current.offsetHeight - (showOverTrigger ? triggerRef.current.offsetHeight : 0))}px` : `${triggerRef.current.offsetTop + (showOverTrigger ? 0 : triggerRef.current.offsetHeight)}px`,
      ...(inlinePosition === EGDSSheetPositionTypes.LEADING && {
        [`--uitk-popover-sheet-inline-pos-ltr`]: `${triggerRef.current.offsetLeft}px`,
        [`--uitk-popover-sheet-inline-pos-rtl`]: `${triggerRef.current.offsetParent.offsetWidth - triggerRef.current.offsetWidth - triggerRef.current.offsetLeft}px`
      }),
      ...(inlinePosition === EGDSSheetPositionTypes.TRAILING && {
        [`--uitk-popover-sheet-inline-pos-ltr`]: `${triggerRef.current.offsetLeft + triggerRef.current.offsetWidth - sheetRef.current.offsetWidth}px`,
        [`--uitk-popover-sheet-inline-pos-rtl`]: `${triggerRef.current.offsetParent.offsetWidth - triggerRef.current.offsetWidth - triggerRef.current.offsetLeft - (sheetRef.current.offsetWidth - triggerRef.current.offsetWidth)}px`
      })
    }),
    ...(width && {
      inlineSize: `${MakeEgdsSizingLength(width)}`
    }),
    ...(offset && {
      '--uitk-popover-sheet-grow-x': `${offset.x}px`,
      '--uitk-popover-sheet-grow-y': `${offset.y}px`
    }),
    ...(autoPosition && intersectionObserverRootRef.current && {
      '--uitk-popover-sheet-max-inline-size': `${intersectionObserverRootRef.current.clientWidth}px`
    })
  };
  const sheetContextValue = React.useMemo(() => ({
    maxHeight
  }), [maxHeight]);
  const l10n = useLocalization();
  return jsx(EGDSSheetContext.Provider, {
    value: sheetContextValue,
    children: jsxs("section", {
      "data-testid": "popover-sheet",
      ref: sheetRef,
      className: sheetClassNames,
      style: posStyle,
      tabIndex: -1,
      role: "dialog",
      "aria-label": ariaLabel ?? l10n.formatText('sheets.rootElement.ariaLabel'),
      onAnimationEnd: onAnimationEndCallback,
      ...attributes,
      children: [hasCloseButton && jsxs(EGDSLayoutFlex, {
        ...layoutProps,
        children: [jsx(EGDSLayoutFlexItem, {
          ...closeButtonLayoutProps,
          children: jsx("div", {
            children: closeButtonElem
          })
        }), childrenElem && jsx(EGDSLayoutFlexItem, {
          children: childrenElem
        })]
      }), !hasCloseButton && childrenElem]
    })
  });
};
EGDSPopoverSheet.displayName = 'EGDSPopoverSheet';
export { EGDSPopoverSheet };